import React, { useEffect, useState } from 'react';
import { HelpCircle, ChevronLeft, ChevronRight, Eye, CheckCircle2, XCircle, PlayCircle } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const pick = (lang, ar, en) => (lang === 'en' && en ? en : ar);

const Option = ({ i, opt, state, onClick, lang }) => {
  const cls = { correct: 'border-emerald-400 bg-emerald-50', wrong: 'border-rose-300 bg-rose-50', idle: 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50' }[state];
  return (
    <button type="button" data-testid={`sim-q-option-${i}`} onClick={onClick}
      className={`w-full min-h-[48px] flex items-center gap-3 px-3 py-2 rounded-xl border text-start text-sm sm:text-[15px] font-semibold text-slate-800 transition-colors ${cls}`}>
      <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-black ${state === 'correct' ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'}`}>{LETTERS[i]}</span>
      <span className="flex-1">{pick(lang, opt.ar, opt.en)}</span>
      {state === 'correct' && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
      {state === 'wrong' && <XCircle className="w-5 h-5 text-rose-500 shrink-0" />}
    </button>
  );
};

export const QuestionsCard = ({ questions, bridge }) => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  useEffect(() => { setIdx(0); setAnswers({}); }, [questions]);
  if (!questions?.length) return null;
  const q = questions[idx];
  const a = answers[q.id] || {};
  const revealed = a.revealed;
  const setA = (patch) => setAnswers(s => ({ ...s, [q.id]: { ...a, ...patch } }));

  const choose = (i) => {
    if (revealed) return;
    setA({ choice: i, revealed: true });
    bridge.send('answer', { questionId: q.id, choice: i, correct: i === q.correct, step: q.step || '' });
    if (q.step) bridge.send('showAnswer', { questionId: q.id, step: q.step });
  };
  const reveal = () => { setA({ revealed: true }); bridge.send('showAnswer', { questionId: q.id, step: q.step || '' }); };
  const stateOf = (i) => (!revealed ? 'idle' : i === q.correct ? 'correct' : i === a.choice ? 'wrong' : 'idle');
  const Next = isRTL ? ChevronLeft : ChevronRight;
  const Prev = isRTL ? ChevronRight : ChevronLeft;

  return (
    <section data-testid="sim-questions-card" className="rounded-3xl border-2 border-blue-600 bg-white overflow-hidden shadow-lg shadow-blue-600/10">
      <header className="flex items-center gap-2 px-5 py-3.5 bg-blue-600 text-white font-black">
        <HelpCircle className="w-5 h-5" />{L('قسم الأسئلة', 'Questions')}
      </header>
      <div className="p-4 sm:p-5 space-y-3">
        <p data-testid="sim-q-counter" className="text-xs font-bold text-blue-700">{L(`السؤال ${idx + 1} من ${questions.length}`, `Question ${idx + 1} of ${questions.length}`)}</p>
        <h3 data-testid="sim-q-text" className="text-slate-900 font-black text-base sm:text-lg leading-snug">{pick(lang, q.textAr, q.textEn)}</h3>
        {(q.options || []).length > 0 && (
          <div className="space-y-2">{q.options.map((o, i) => <Option key={i} i={i} opt={o} lang={lang} state={stateOf(i)} onClick={() => choose(i)} />)}</div>
        )}
        {revealed && (
          <div data-testid="sim-q-explain" className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-sm text-slate-700 leading-relaxed">
            {a.choice !== undefined && q.correct >= 0 && (
              <p className={`font-black mb-1 ${a.choice === q.correct ? 'text-emerald-700' : 'text-rose-600'}`}>{a.choice === q.correct ? L('إجابة صحيحة!', 'Correct!') : L('إجابة غير صحيحة', 'Not quite')}</p>
            )}
            {q.correct >= 0 && <p className="font-bold text-slate-900 mb-1">{L('الإجابة الصحيحة:', 'Correct answer:')} {pick(lang, q.options[q.correct]?.ar, q.options[q.correct]?.en)}</p>}
            <p className="whitespace-pre-line">{pick(lang, q.explainAr, q.explainEn)}</p>
            {q.step && bridge.can('goto') && (
              <button type="button" data-testid="sim-q-replay" onClick={() => bridge.send('goto', { step: q.step, questionId: q.id })} className="mt-2 inline-flex items-center gap-1.5 text-blue-700 font-bold text-sm"><PlayCircle className="w-4 h-4" />{L('إعادة العرض في المشهد', 'Replay in the scene')}</button>
            )}
          </div>
        )}
        <button type="button" data-testid="sim-q-reveal" onClick={reveal} disabled={revealed}
          className="w-full h-12 rounded-xl border-2 border-blue-200 text-blue-700 font-bold inline-flex items-center justify-center gap-2 hover:bg-blue-50 disabled:opacity-50">
          <Eye className="w-5 h-5" />{L('عرض الإجابة', 'Show answer')}
        </button>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button type="button" data-testid="sim-q-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="h-12 rounded-xl border-2 border-blue-200 text-blue-700 font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"><Prev className="w-4 h-4" />{L('السابق', 'Previous')}</button>
          <button type="button" data-testid="sim-q-next" disabled={idx === questions.length - 1} onClick={() => setIdx(idx + 1)} className="h-12 rounded-xl bg-blue-600 text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40">{L('التالي', 'Next')}<Next className="w-4 h-4" /></button>
        </div>
        <div className="flex justify-center flex-wrap gap-1.5 pt-1" data-testid="sim-q-dots">
          {questions.map((x, i) => (
            <button key={x.id} type="button" aria-label={`${i + 1}`} onClick={() => setIdx(i)} className="p-2 -m-0.5">
              <span className={`block h-2.5 rounded-full transition-all ${i === idx ? 'w-6 bg-blue-600' : answers[x.id]?.revealed ? 'w-2.5 bg-emerald-400' : 'w-2.5 bg-slate-300'}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
